import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { notification, Tabs } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { BackLink } from '@/components/layout/BackLink'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { fetchRobotPositions } from '@/api/robot-positions.api'
import {
  updateRobotLocation,
  fetchRobotHomeLocation,
  addRobotHomeLocation,
  updateRobotHomeLocation,
} from '@/api/robot-locations.api'
import { updateScheduleByLocation } from '@/api/robot-schedule.api'
import { getApiErrorMessage } from '@/utils/api-error'

import { RobotEventsTab } from './robot-event/RobotEventsTab'
import { pickVoiceId } from './robot-position-setup/helpers'
import { normalizeYaw, toHeadingLabel } from './robot-position-setup/slam'
import type {
  Position,
  SlamPoint,
  TimeRange,
} from './robot-position-setup/types'

import { usePositionScheduleSync } from './hook/hook position/usePositionScheduleSync'
import { usePositionSelectionState } from './hook/hook position/usePositionSelectionState'
import { useSlamMapState } from './hook/hook slam/useSlamMapState'
import { usePositionLocationMutations } from './hook/hook position/usePositionLocationMutations'
import { RobotPositionSetupTab } from './robot-position-setup/RobotPositionSetupTab/RobotPositionSetupTab'
import { RobotConfigTab } from './robot-position-setup/RobotConfigTab/RobotConfigTab'
import { useRobotConfigForm } from './hook/hook robot/useRobotConfigForm'
import { useRobotMapRetry } from './hook/hook robot/useRobotMapRetry'
import { useRobotPositionEditor } from './hook/hook robot/useRobotPositionEditor'

type ApiPosition = Pick<
  Position,
  'id' | 'name' | 'x' | 'y' | 'rawX' | 'rawY' | 'yaw'
>
const POSITION_MATCH_EPS = 0.0001
const POINT_UPDATE_EPS = 0.00001

export const TABS_CLASS = [
  '[&_.ant-tabs-nav]:!mb-0',
  '[&_.ant-tabs-tab]:!px-5 [&_.ant-tabs-tab]:!py-2.5 [&_.ant-tabs-tab]:!rounded-t-xl',
  '[&_.ant-tabs-tab+.ant-tabs-tab]:!ml-1',
  '[&_.ant-tabs-ink-bar]:!hidden',
  '[&_.ant-tabs-nav-wrap]:h-14',
  '[&_.ant-tabs-tab_.ant-tabs-tab-btn]:!text-gray-600',
  '[&_.ant-tabs-tab.ant-tabs-tab-active]:!bg-[#fbeae4]',
  '[&_.ant-tabs-tab.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[#C97B63]',
  '[&_.ant-tabs-tab.ant-tabs-tab-active_.ant-tabs-tab-btn]:!font-semibold',
].join(' ')

export default function RobotPositionSetupPage() {
  const params = useParams({ strict: false })
  const robotId = params.id || ''

  const [activeTab, setActiveTab] = useState('1')

  const [positions, setPositions] = useState<Position[]>([])
  const [initialPositions, setInitialPositions] = useState<Position[]>([])
  const positionsRef = useRef<Position[]>([])
  const [mapRotation, setMapRotation] = useState<number>(0)
  const [homeYaw, setHomeYaw] = useState<number>(0)
  const [isHomePlacementRequired, setIsHomePlacementRequired] =
    useState<boolean>(false)
  const [isHomeSaveLocked, setIsHomeSaveLocked] = useState<boolean>(false)
  const [isHomeExistedOnServer, setIsHomeExistedOnServer] =
    useState<boolean>(false)
  const [homeAnchorPoint, setHomeAnchorPoint] = useState<SlamPoint | null>(null)

  const [pendingClickCoords, setPendingClickCoords] =
    useState<SlamPoint | null>(null)
  const [pendingAddSuccessToastId, setPendingAddSuccessToastId] = useState<
    string | null
  >(null)
  const [addModeTimeRanges, setAddModeTimeRanges] = useState<TimeRange[]>([])
  const editingPositionNameRef = useRef<{ id: string; name: string } | null>(
    null,
  )

  const {
    robot,
    robotForm,
    robotInitialValues,
    isUpdatingConfig,
    setLiveValues,
    updateConfig,
    handleWorkingScheduleRemove,
    refetchDetail,
  } = useRobotConfigForm(robotId)

  const {
    data: loadedPositions,
    isPending: isLoadingPositions,
    refetch: refetchPositions,
  } = useQuery({
    queryKey: ['robot-positions-with-schedules', robotId],
    queryFn: async ({ signal }): Promise<Position[]> => {
      if (!robotId) return []
      const data = await fetchRobotPositions(
        robotId,
        0,
        10,
        undefined,
        signal,
        robot?.mapFileUrl,
      )
      return (data as ApiPosition[]).map((p) => ({
        ...p,
        timeRanges: [],
        initialScheduleIds: [],
        scheduleLoaded: false,
      }))
    },
    enabled: !!robotId && !!robot?.mapFileUrl && activeTab === '2',
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const { data: homeLocationData, isFetching: isLoadingHomeLocation } =
    useQuery({
      queryKey: ['robot-home-location', robotId],
      queryFn: ({ signal }) => fetchRobotHomeLocation(robotId, signal),
      enabled: !!robotId && activeTab === '2',
      refetchOnMount: 'always',
      staleTime: 0,
      gcTime: 0,
    })

  useEffect(() => {
    if (activeTab !== '2' || isLoadingHomeLocation) return

    if (homeLocationData) {
      setHomeAnchorPoint({
        x: homeLocationData.longitude,
        y: homeLocationData.latitude,
      })
      setHomeYaw(homeLocationData.yaw)
      setMapRotation(homeLocationData.yaw)
      setIsHomeExistedOnServer(true)
      setIsHomePlacementRequired(false)
      setIsHomeSaveLocked(true)
    } else {
      setIsHomeExistedOnServer(false)
      setIsHomeSaveLocked(false)
      setIsHomePlacementRequired(true)
    }
  }, [activeTab, homeLocationData, isLoadingHomeLocation])

  const { isRetrying, retrySeconds, handleRetrySendMap } = useRobotMapRetry(
    robotId,
    refetchDetail,
    refetchPositions,
  )

  const handleSlamLoadError = useCallback(() => {
    notification.error({
      title: 'Không thể tải sơ đồ robot (SLAM) từ mapFileUrl',
    })
  }, [])

  const {
    slamJson,
    hasMap,
    hasSlamLoadError,
    isPositionSetupLoading,
    showPositionSetupContent,
  } = useSlamMapState({
    mapFileUrl: activeTab === '2' ? robot?.mapFileUrl : undefined,
    activeTab,
    isLoadingPositions,
    onLoadError: handleSlamLoadError,
  })

  useEffect(() => {
    if (slamJson.home?.yawDeg !== undefined) {
      setHomeYaw(slamJson.home.yawDeg)
    }
  }, [slamJson])

  const slamBounds = useMemo(() => {
    if (!slamJson.bounds) return { minX: 0, maxX: 1, minY: 0, maxY: 1 }
    const { minX, maxX, minY, maxY } = slamJson.bounds
    return {
      minX,
      maxX: maxX === minX ? maxX + 1 : maxX,
      minY,
      maxY: maxY === minY ? maxY + 1 : maxY,
    }
  }, [slamJson])

  const percentToSlamPoint = useCallback(
    (p: SlamPoint): SlamPoint => {
      const rawX =
        slamBounds.minX + (p.x / 100) * (slamBounds.maxX - slamBounds.minX)
      const rawY =
        slamBounds.maxY - (p.y / 100) * (slamBounds.maxY - slamBounds.minY)
      return {
        x: rawX,
        y: rawY,
      }
    },
    [slamBounds],
  )

  const getRawPointFromPosition = useCallback(
    (p: Pick<Position, 'x' | 'y' | 'rawX' | 'rawY'>): SlamPoint => {
      if (
        typeof p.rawX === 'number' &&
        typeof p.rawY === 'number' &&
        Number.isFinite(p.rawX) &&
        Number.isFinite(p.rawY)
      ) {
        return { x: p.rawX, y: p.rawY }
      }
      return percentToSlamPoint({ x: p.x, y: p.y })
    },
    [percentToSlamPoint],
  )

  const {
    selectedPositionId,
    setSelectedPositionId,
    selectedCoordinate,
    setSelectedCoordinate,
    positionPanelTab,
    setPositionPanelTab,
    directionRotateMode,
    setDirectionRotateMode,
    selectedPosition,
    positionsAtSelectedCoordinate,
    showPositionPanel,
    visibleMarkers,
    directionOverlayMarkerId,
    directionOverlayYaw,
    handleMarkerClick,
    handleSelectPositionFromCoordinate,
    handleEditPositionDirection,
    handleClosePositionDetail,
  } = usePositionSelectionState({
    positions,
    activeTab,
    isAddingLocation: !!pendingClickCoords,
    positionMatchEps: POSITION_MATCH_EPS,
  })

  // Khởi tạo custom hook quản lý Edit/Add state
  const {
    isEditingMode,
    setIsEditingMode,
    isAddmode,
    setIsAddmode,
    addFormState,
    setAddFormState,
    confirmState,
    setConfirmState,
    editingSnapshotRef,
    handleCancelEditOrAdd,
    executeWithConfirmIfBusy,
    handleSelectPositionWithModeReset,
    handleStartUpdatePosition,
    handleStartAddPosition,
  } = useRobotPositionEditor({
    initialPositions,
    setPositions,
    homeYaw,
    setHomeYaw,
    setMapRotation,
    selectedPositionId,
    handleSelectPositionFromCoordinate,
    setDirectionRotateMode,
  })

  const {
    isAddingLocation,
    isDeletingLocation,
    handleDeletePosition,
    handleMarkerYawChange,
    handleMenuAddNew,
  } = usePositionLocationMutations({
    robotId,
    pendingClickCoords,
    setPendingClickCoords,
    positionsRef,
    setPositions,
    setSelectedCoordinate,
    setSelectedPositionId,
    setDirectionRotateMode,
    getRawPointFromPosition,
    toRawPoint: percentToSlamPoint,
    onAddLocationSuccess: (createdId) => setPendingAddSuccessToastId(createdId),
  })

  const { loadingSchedulePositionId, syncPositionSchedules } =
    usePositionScheduleSync({
      robotId,
      activeTab,
      positionPanelTab,
      selectedPositionId,
      positions,
      setPositions,
      onLoadError: (error) =>
        notification.error({
          title: getApiErrorMessage(
            error,
            'Có lỗi xảy ra khi tải lịch trình vị trí.',
          ),
        }),
    })

  useEffect(() => {
    if (loadedPositions) {
      setPositions(loadedPositions)
      setInitialPositions(loadedPositions)
      const homePos = loadedPositions.find((p) => p.id === 'home')
      if (homePos && typeof homePos.yaw === 'number') {
        setMapRotation(homePos.yaw)
        setHomeYaw(homePos.yaw)
      }

      if (!homePos) {
        setSelectedPositionId('')
      }
    }
  }, [loadedPositions, setSelectedPositionId])

  useEffect(() => {
    positionsRef.current = positions
  }, [positions])

  useEffect(() => {
    if (!pendingAddSuccessToastId || !showPositionSetupContent) return
    const hasCreatedMarker = positions.some(
      (p) => p.id === pendingAddSuccessToastId,
    )
    if (!hasCreatedMarker) return

    const frameId = window.requestAnimationFrame(() => {
      notification.success({ title: 'Thêm vị trí vào sơ đồ thành công' })
      setPendingAddSuccessToastId(null)
    })
    return () => window.cancelAnimationFrame(frameId)
  }, [pendingAddSuccessToastId, positions, showPositionSetupContent])

  // Lưu Vị trí (Xử lý cho cả Add và Update)
  const handleSavePosition = async () => {
    if (isHomePlacementRequired) {
      if (!selectedCoordinate) {
        notification.error({ title: 'Vui lòng chấm tọa độ Home trước khi lưu' })
        return
      }

      try {
        const payload = {
          longitude: selectedCoordinate.x,
          latitude: selectedCoordinate.y,
          yaw: homeYaw,
        }
        if (isHomeExistedOnServer) {
          await updateRobotHomeLocation(robotId, payload)
        } else {
          await addRobotHomeLocation(robotId, payload)
        }
      } catch (error) {
        notification.error({
          title: getApiErrorMessage(error, 'Lưu tọa độ Home thất bại'),
        })
        return
      }

      setSelectedPositionId('')
      setPendingClickCoords(null)
      setHomeAnchorPoint(selectedCoordinate)
      setIsHomePlacementRequired(false)
      setIsHomeSaveLocked(true)
      setIsHomeExistedOnServer(true)
      setIsEditingMode(false)
      setDirectionRotateMode(false)
      editingSnapshotRef.current = null

      notification.success({ title: 'Đã lưu tọa độ Home' })
      return
    }

    if (isAddmode) {
      if (!addFormState.name.trim()) {
        notification.error({ title: 'Vui lòng nhập tên vị trí' })
        return
      }
      const currentCoords = selectedCoordinate || pendingClickCoords
      const createdId = await handleMenuAddNew({
        name: addFormState.name,
        yaw: addFormState.yaw,
        coords: currentCoords,
      })

      if (!createdId) {
        notification.error({ title: 'Chưa có tọa độ để thêm vị trí mới' })
        return
      }

      if (addModeTimeRanges.length > 0) {
        try {
          await updateScheduleByLocation(robotId, {
            newScheduleList: addModeTimeRanges
              .filter((item) => item.from && item.to)
              .map((item) => ({
                startTime: item.from,
                endTime: item.to,
                locationId: createdId,
              })),
            updateScheduleList: [],
            deleteScheduleIdList: [],
          })
        } catch (error) {
          notification.error({
            title: getApiErrorMessage(
              error,
              'Đã tạo vị trí nhưng lưu lịch trình thất bại',
            ),
          })
        }
      }

      await refetchPositions()
      setSelectedPositionId(createdId)
      setPositionPanelTab('info')
      setAddModeTimeRanges([])

      setIsAddmode(false)
      setDirectionRotateMode(false)
      return
    }

    if (isEditingMode && selectedPositionId === 'home') {
      if (homeAnchorPoint) {
        try {
          await updateRobotHomeLocation(robotId, {
            longitude: homeAnchorPoint.x,
            latitude: homeAnchorPoint.y,
            yaw: homeYaw,
          })
        } catch (error) {
          notification.error({
            title: getApiErrorMessage(error, 'Cập nhật hướng Home thất bại'),
          })
          return
        }
      }
      setMapRotation(homeYaw)
      setIsEditingMode(false)
      setDirectionRotateMode(false)
      editingSnapshotRef.current = null
      notification.success({ title: 'Đã cập nhật hướng Home' })
      return
    }

    if (isEditingMode && selectedPosition) {
      const rawPoint = getRawPointFromPosition(selectedPosition)
      const targetYaw = selectedPosition.yaw || 0
      await updateRobotLocation(robotId, {
        id: selectedPosition.id,
        name: selectedPosition.name || '',
        longitude: rawPoint.x || 0,
        latitude: rawPoint.y || 0,
        yaw: targetYaw,
        status: 1,
      })

      await refetchPositions()
      setIsEditingMode(false)
      setDirectionRotateMode(false)
      editingSnapshotRef.current = null
    }
  }

  const handleSlamClick = (p: SlamPoint) => {
    const isSamePoint = (a: SlamPoint | null, b: SlamPoint) =>
      !!a &&
      Math.abs(a.x - b.x) <= POINT_UPDATE_EPS &&
      Math.abs(a.y - b.y) <= POINT_UPDATE_EPS

    if (isHomePlacementRequired) {
      if (selectedPositionId !== 'home') {
        setSelectedPositionId('home')
      }
      if (!isEditingMode) {
        setIsEditingMode(true)
      }
      if (directionRotateMode) {
        setDirectionRotateMode(false)
      }
      if (!isSamePoint(homeAnchorPoint, p)) {
        setHomeAnchorPoint(p)
      }
    }

    if (!isSamePoint(pendingClickCoords, p)) {
      setPendingClickCoords(p)
    }
    if (!isSamePoint(selectedCoordinate, p)) {
      setSelectedCoordinate(p)
    }
    if (positionPanelTab !== 'info') {
      setPositionPanelTab('info')
    }
  }

  const handleRequestDeletePosition = (posId: string) => {
    executeWithConfirmIfBusy(() => {
      setConfirmState({
        isOpen: true,
        title: 'Xóa vị trí?',
        description: 'Hành động này sẽ xóa vị trí khỏi sơ đồ robot.',
        confirmText: 'Xóa',
        cancelText: 'Hủy',
        variant: 'danger',
        onConfirm: () => handleDeletePosition(posId),
      })
    })
  }

  const formatYawLabel = (yaw: number) =>
    `${toHeadingLabel(yaw)} (${normalizeYaw(yaw).toFixed(5)})`

  const handleSavePositionWithConfirm = async () => {
    const isHomeFlow = isHomePlacementRequired
    setConfirmState({
      isOpen: true,
      title: isHomeFlow ? 'Xác nhận lưu Home?' : 'Xác nhận lưu thay đổi?',
      description: isHomeFlow
        ? 'Sau khi xác nhận, vị trí Home sẽ được lưu và không thể thao tác đặt lại Home trong phiên này.'
        : 'Bạn có chắc chắn muốn lưu các thay đổi hiện tại không?',
      confirmText: 'Lưu',
      cancelText: 'Hủy',
      variant: 'info',
      onConfirm: () => {
        void handleSavePosition()
      },
    })
  }

  const resetRobotConfigFormState = useCallback(() => {
    robotForm.resetFields()
    if (robotInitialValues) {
      robotForm.setFieldsValue({
        ...robotInitialValues,
        voice: pickVoiceId(robot?.voice),
      })
    }
    setLiveValues({})
  }, [robotForm, robotInitialValues, robot?.voice, setLiveValues])

  const preparePositionSetupTab = useCallback(() => {
    setIsHomeSaveLocked(false)
    setIsHomeExistedOnServer(false)
    setIsHomePlacementRequired(false)
    setHomeAnchorPoint(null)
    setSelectedCoordinate(null)
    setSelectedPositionId('')
    setPendingClickCoords(null)
    setIsEditingMode(false)
    setDirectionRotateMode(false)
  }, [
    setSelectedCoordinate,
    setSelectedPositionId,
    setDirectionRotateMode,
    setIsEditingMode,
  ])

  const resetPositionSetupState = useCallback(() => {
    setPositions(initialPositions)
    const homePos = initialPositions.find((p) => p.id === 'home')
    if (homePos && typeof homePos.yaw === 'number') {
      setMapRotation(homePos.yaw)
      setHomeYaw(homePos.yaw)
    }
    setPendingClickCoords(null)
    setSelectedPositionId('')
    setSelectedCoordinate(null)
    setPositionPanelTab('info')
    setDirectionRotateMode(false)
    setIsEditingMode(false)
    setIsAddmode(false)
    setAddModeTimeRanges([])
    editingSnapshotRef.current = null
    setIsHomePlacementRequired(false)
    setHomeAnchorPoint(null)
    setIsHomeSaveLocked(false)
    setIsHomeExistedOnServer(false)
  }, [
    initialPositions,
    setSelectedPositionId,
    setSelectedCoordinate,
    setPositionPanelTab,
    setDirectionRotateMode,
    setIsEditingMode,
    setIsAddmode,
  ])

  const handleTabChange = (nextTab: string) => {
    if (nextTab === activeTab) return

    executeWithConfirmIfBusy(() => {
      if (activeTab === '1') {
        resetRobotConfigFormState()
      }
      if (activeTab === '2') {
        resetPositionSetupState()
      }

      if (nextTab === '2') {
        preparePositionSetupTab()
      }

      setActiveTab(nextTab)
    })
  }

  const handleRefreshPositions = useCallback(() => {
    executeWithConfirmIfBusy(() => {
      refetchPositions()
      notification.success({
        title: 'Đã làm mới danh sách vị trí',
      })
    })
  }, [executeWithConfirmIfBusy, refetchPositions])

  // GIỮ LẠI TỌA ĐỘ CŨ ĐỂ HIỂN THỊ PANEL NGAY LẬP TỨC
  const handleRepickHome = useCallback(() => {
    executeWithConfirmIfBusy(() => {
      setSelectedPositionId('home')
      const currentHomeCoord = homeAnchorPoint ||
        selectedCoordinate || { x: 0, y: 0 }
      setSelectedCoordinate(currentHomeCoord)
      setPendingClickCoords(currentHomeCoord)
      setIsHomePlacementRequired(true)
      setIsEditingMode(true)
      setDirectionRotateMode(false)
    })
  }, [
    executeWithConfirmIfBusy,
    homeAnchorPoint,
    selectedCoordinate,
    setSelectedPositionId,
    setSelectedCoordinate,
    setIsEditingMode,
    setDirectionRotateMode,
  ])

  const handleMarkerYawChangeFromCanvas = useCallback(
    (posId: string, nextYaw: number) => {
      if (isAddmode) {
        setAddFormState((prev) => ({ ...prev, yaw: nextYaw }))
      } else if (posId === 'home') {
        setHomeYaw(nextYaw * (180 / Math.PI))
      } else {
        handleMarkerYawChange(posId, nextYaw)
      }
    },
    [isAddmode, setAddFormState, handleMarkerYawChange],
  )

  const handleMarkerYawCommitFromCanvas = useCallback(
    (posId: string, nextYaw: number) => {
      if (isAddmode) {
        setAddFormState((prev) => ({ ...prev, yaw: nextYaw }))
      } else if (posId === 'home') {
        setHomeYaw(nextYaw * (180 / Math.PI))
      } else {
        setPositions((prev) =>
          prev.map((p) => (p.id === posId ? { ...p, yaw: nextYaw } : p)),
        )
      }
    },
    [isAddmode, setAddFormState],
  )

  const handleStartEditHomeDirection = useCallback(() => {
    handleStartUpdatePosition('home', positions)
  }, [handleStartUpdatePosition, positions])

  const handleClosePositionDetailWithConfirm = useCallback(() => {
    executeWithConfirmIfBusy(() => {
      if (isHomePlacementRequired) {
        setIsHomePlacementRequired(false)
        if (isHomeExistedOnServer) {
          setIsHomeSaveLocked(true)
        }
        if (homeLocationData) {
          const originalHomeCoords = {
            x: homeLocationData.longitude,
            y: homeLocationData.latitude,
          }
          setHomeAnchorPoint(originalHomeCoords)
          setSelectedCoordinate(originalHomeCoords)
          setPendingClickCoords(originalHomeCoords)
        }
      }
      if (isAddmode) {
        setIsAddmode(false)
        setAddModeTimeRanges([])
      }
      handleClosePositionDetail(isEditingMode, initialPositions, setPositions)
    })
  }, [
    executeWithConfirmIfBusy,
    handleClosePositionDetail,
    isEditingMode,
    initialPositions,
    isAddmode,
  ])

  const handlePositionNameChange = useCallback(
    (nextName: string) => {
      if (isAddmode) {
        setAddFormState((prev) => ({ ...prev, name: nextName }))
        return
      }
      if (!selectedPosition) return
      setPositions((prev) =>
        prev.map((p) =>
          p.id === selectedPosition.id ? { ...p, name: nextName } : p,
        ),
      )
    },
    [isAddmode, selectedPosition, setAddFormState],
  )

  const handleSchedulesChange = useCallback(() => {
    if (selectedPosition) {
      syncPositionSchedules(selectedPosition.id)
    }
  }, [selectedPosition, syncPositionSchedules])

  // Dữ liệu đối tượng vị trí chọn để truyền xuống Panel chi tiết
  const effectiveSelectedPosition = useMemo<Position | null>(() => {
    if (isHomePlacementRequired && selectedCoordinate) {
      return {
        id: 'home',
        name: 'Home',
        x: selectedCoordinate.x,
        y: selectedCoordinate.y,
        rawX: selectedCoordinate.x,
        rawY: selectedCoordinate.y,
        yaw: homeYaw,
        timeRanges: [],
        initialScheduleIds: [],
        scheduleLoaded: true,
      }
    }

    if (isAddmode) {
      const coords = selectedCoordinate || pendingClickCoords || { x: 0, y: 0 }
      return {
        id: 'temp_add_id',
        name: addFormState.name,
        x: coords.x,
        y: coords.y,
        rawX: coords.x,
        rawY: coords.y,
        yaw: addFormState.yaw,
        timeRanges: addModeTimeRanges,
        initialScheduleIds: [],
        scheduleLoaded: true,
      }
    }

    if (
      !isHomePlacementRequired &&
      selectedPositionId === 'home' &&
      homeAnchorPoint
    ) {
      return {
        id: 'home',
        name: 'Home',
        x: homeAnchorPoint.x,
        y: homeAnchorPoint.y,
        rawX: homeAnchorPoint.x,
        rawY: homeAnchorPoint.y,
        yaw: homeYaw,
        timeRanges: [],
        initialScheduleIds: [],
        scheduleLoaded: true,
      }
    }

    if (!selectedPosition) return null
    if (selectedPosition.id === 'home') {
      return {
        ...selectedPosition,
        yaw: homeYaw,
      }
    }
    return selectedPosition
  }, [
    isHomePlacementRequired,
    isAddmode,
    selectedCoordinate,
    pendingClickCoords,
    addFormState,
    addModeTimeRanges,
    selectedPosition,
    selectedPositionId,
    homeAnchorPoint,
    homeYaw,
  ])

  const effectiveSlamJson = useMemo(() => {
    if (slamJson.home) {
      return {
        ...slamJson,
        home: {
          ...slamJson.home,
          yawDeg: homeYaw,
        },
      }
    }

    const homeFromPosition = positions.find(
      (position) => position.id === 'home',
    )
    if (!homeFromPosition) return slamJson

    return {
      ...slamJson,
      home: {
        x: homeFromPosition.rawX ?? homeFromPosition.x,
        y: homeFromPosition.rawY ?? homeFromPosition.y,
        yawDeg: homeYaw,
      },
    }
  }, [slamJson, positions, homeYaw])

  return (
    <div className="space-y-4 pb-4">
      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        description={confirmState.description}
        cancelText={confirmState.cancelText}
        confirmText={confirmState.confirmText}
        variant={confirmState.variant}
        isLoading={false}
        onClose={() => setConfirmState({ isOpen: false })}
        onConfirm={() => {
          confirmState.onConfirm?.()
          setConfirmState({ isOpen: false })
        }}
      />
      <BackLink to="/robots" label="Quay lại" title="THIẾT LẬP CẤU HÌNH" />

      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_18px_60px_rgba(26,26,26,0.06)]">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          destroyOnHidden
          className={`${TABS_CLASS}`}
          items={[
            {
              key: '1',
              label: 'Cấu hình Robot',
              children: (
                <RobotConfigTab
                  form={robotForm}
                  initialValues={robotInitialValues}
                  isUpdating={isUpdatingConfig}
                  onWorkingScheduleRemove={handleWorkingScheduleRemove}
                  onValuesChange={(_changed, all) => setLiveValues(all)}
                  onSubmit={(values) => updateConfig(values)}
                />
              ),
            },
            {
              key: '2',
              label: 'Thiết lập vị trí',
              children: (
                <RobotPositionSetupTab
                  robotId={robotId}
                  activeTab={activeTab}
                  showPositionSetupContent={showPositionSetupContent}
                  showPositionPanel={showPositionPanel}
                  selectedCoordinate={selectedCoordinate}
                  positionsAtSelectedCoordinate={positionsAtSelectedCoordinate}
                  selectedPositionId={selectedPositionId}
                  isDeletingLocation={isDeletingLocation}
                  hasMap={hasMap}
                  hasSlamLoadError={hasSlamLoadError}
                  isPositionSetupLoading={isPositionSetupLoading}
                  slamJson={effectiveSlamJson}
                  visibleMarkers={visibleMarkers}
                  pendingClickCoords={pendingClickCoords}
                  directionRotateMode={directionRotateMode}
                  isAddingLocation={isAddingLocation}
                  directionOverlayMarkerId={directionOverlayMarkerId}
                  directionOverlayYaw={directionOverlayYaw}
                  isRetrying={isRetrying}
                  retrySeconds={retrySeconds}
                  positionPanelTab={positionPanelTab}
                  selectedPosition={effectiveSelectedPosition}
                  loadingSchedulePositionId={loadingSchedulePositionId}
                  tabsClassName={TABS_CLASS}
                  editingPositionNameRef={editingPositionNameRef}
                  isEditingMode={
                    isEditingMode ||
                    (isHomePlacementRequired && !!selectedCoordinate)
                  }
                  isAddmode={isAddmode}
                  isHomePlacementRequired={isHomePlacementRequired}
                  homePlacementPoint={homeAnchorPoint}
                  mapRotation={mapRotation}
                  backgroundImageUrl={robot?.diagramInfo?.fileUrl ?? null}
                  backgroundWidthM={robot?.diagramInfo?.width}
                  backgroundHeightM={robot?.diagramInfo?.height}
                  effectiveSelectedPosition={effectiveSelectedPosition}
                  homeYaw={homeYaw}
                  onSelectPosition={handleSelectPositionWithModeReset}
                  onUpdatePosition={(posId) =>
                    handleStartUpdatePosition(posId, positions)
                  }
                  onAddPosition={handleStartAddPosition}
                  onLocalTimeRangesChange={setAddModeTimeRanges}
                  onRequestDelete={handleRequestDeletePosition}
                  onRefreshPositions={handleRefreshPositions}
                  onRepickHome={handleRepickHome}
                  onEditHomeDirection={handleStartEditHomeDirection}
                  onSlamClick={handleSlamClick}
                  onMarkerClick={handleMarkerClick}
                  onMarkerEditDirection={handleEditPositionDirection}
                  onMarkerYawChange={handleMarkerYawChangeFromCanvas}
                  onMarkerYawCommit={handleMarkerYawCommitFromCanvas}
                  onConfirmAddNew={async () => {
                    await handleMenuAddNew()
                  }}
                  onRetrySendMap={handleRetrySendMap}
                  onClosePositionDetail={handleClosePositionDetailWithConfirm}
                  onPositionPanelTabChange={setPositionPanelTab}
                  onPositionNameChange={handlePositionNameChange}
                  onSchedulesChange={handleSchedulesChange}
                  formatYawLabel={formatYawLabel}
                  onCancelEdit={() => {
                    if (isHomePlacementRequired) {
                      setIsHomePlacementRequired(false)
                      if (isHomeExistedOnServer) {
                        setIsHomeSaveLocked(true)
                      }
                      if (homeLocationData) {
                        setHomeAnchorPoint({
                          x: homeLocationData.longitude,
                          y: homeLocationData.latitude,
                        })
                        setSelectedCoordinate({
                          x: homeLocationData.longitude,
                          y: homeLocationData.latitude,
                        })
                      }
                    }
                    handleCancelEditOrAdd()
                    setAddModeTimeRanges([])
                  }}
                  onSavePosition={handleSavePositionWithConfirm}
                />
              ),
            },
            {
              key: '3',
              label: 'Sự kiện',
              children: (
                <div className="p-4">
                  <RobotEventsTab robotId={robotId} />
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  )
}
